import React from 'react';
import { Text } from '@react-pdf/renderer';
import { Common } from './constants';
import { toast } from 'react-toastify';

type RenderMode = 'html' | 'pdf';

export const formatMessageWithBoldTitle = (message: string, renderAs: RenderMode = 'html'): React.ReactNode => {
  const regex = /##(.*?)##/;
  const match = message.match(regex);

  if (!match) {
    return renderAs === 'pdf' ? <Text>{message}</Text> : <div>{message}</div>;
  }
  const [fullMatch, boldText] = match;
  const parts = message.split(fullMatch);

  if (renderAs === 'pdf') {
    return (
      <Text>
        {parts[0]}
        <Text style={{ fontWeight: 'bold' }}>{boldText}</Text>
        {parts[1]}
      </Text>
    );
  } else {
    return (
      <>
        {parts[0]}
        <strong>{boldText}</strong>
        {parts[1]}
      </>
    );
  }
};

export const maskEmail = (email: string): string => {
  try {
    const [local, domainPart] = email.split('@');
    if (!domainPart) {
      return email;
    }

    const domainMatch = domainPart.match(/^(.)([^.]*)(\..+)$/);
    if (!domainMatch) {
      return email;
    }

    const [, firstChar, restBeforeDot, domainSuffix] = domainMatch;

    const maskedLocal = local.length <= 4 ? local : local.slice(0, 4) + '*'.repeat(local.length - 4);

    const maskedDomain = firstChar + '*'.repeat(restBeforeDot.length);

    return `${maskedLocal}@${maskedDomain}${domainSuffix}`;
  } catch {
    return email;
  }
};

export const maskPhoneNumber = (phoneNumber: string) => {
  if (!phoneNumber) return '';

  const formattedPhoneNumber = phoneNumber.replace(/\D/g, '');

  if (formattedPhoneNumber.length <= 4) return formattedPhoneNumber;

  const lastFour = formattedPhoneNumber.slice(-4);
  const masked = '*'.repeat(formattedPhoneNumber.length - 4);

  return masked + lastFour;
};

export const getISDCodeFromRegion = (selectedRegion?: string): string => {
  if (!selectedRegion) {
    return Common.defaultCountry.ISD_CODE;
  }

  return Common.REGION_TO_ISD_CODE[selectedRegion] ?? Common.defaultCountry.ISD_CODE;
};

export const getCountryCodeFromRegion = (selectedRegion?: string): string => {
  if (!selectedRegion) {
    return Common.defaultCountry.COUNTRY_CODE;
  }

  return Common.REGION_TO_COUNTRY_CODE[selectedRegion] ?? Common.defaultCountry.COUNTRY_CODE;
};

/**
 * Encrypts a plaintext password for backend validation.
 * @param {string} plainTextPassword - The user's input password.
 * @param {string} base64PublicKey - The raw Base64 public key from config.
 * @returns {Promise<string>} - The Base64 encoded encrypted password.
 */
export const encryptPassword = async (plainTextPassword: string, base64PublicKey: string) => {
  try {
    // 1. Decode the Base64 key string into an ArrayBuffer
    const binaryDerString = window.atob(base64PublicKey);
    const binaryDer = new Uint8Array(binaryDerString.length);
    for (let i = 0; i < binaryDerString.length; i++) {
      binaryDer[i] = binaryDerString.charCodeAt(i);
    }
    // 2. Import the Public Key
    const publicKey = await window.crypto.subtle.importKey(
      'spki',
      binaryDer.buffer,
      { name: 'RSA-OAEP', hash: 'SHA-256' },
      true,
      ['encrypt'],
    );
    // 3. Encrypt the password using OAEP with SHA-256
    const encodedPassword = new TextEncoder().encode(plainTextPassword);
    const encryptedBuffer = await window.crypto.subtle.encrypt({ name: 'RSA-OAEP' }, publicKey, encodedPassword);

    // 4. Convert the encrypted buffer back to a Base64 string
    const encryptedArray = Array.from(new Uint8Array(encryptedBuffer));
    return window.btoa(String.fromCharCode.apply(null, encryptedArray));
  } catch (error) {
    console.error('Encryption failed:', error);
    throw error;
  }
};

export const handleErrorMessage = (error) => {
  toast.error(error.response?.data?.message || error.toString());
};

export const isSafeHttpUrl = (url?: string): boolean => {
  if (!url) return false;
  try {
    const parsedURL = new URL(url, window.location.origin);
    return parsedURL.protocol === 'http:' || parsedURL.protocol === 'https:';
  } catch {
    return false;
  }
};
